import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  FaBriefcase,
  FaGavel,
  FaHome,
  FaBuilding,
  FaUsers,
  FaGlobe,
  FaHeartbeat,
  FaTree,
  FaLightbulb,
  FaCalculator,
  FaUniversity,
  FaHardHat,
  FaShoppingCart,
  FaLandmark,
  FaBalanceScale,
  FaAnchor,
  FaNewspaper,
  FaMapMarkedAlt,
  FaScroll,
  FaArrowRight,
  FaSearch,
} from 'react-icons/fa';
import { PRACTICE_AREAS } from '../config/content';
import SEO from '../components/SEO';
import styles from './PracticeAreas.module.css';

/* ── Reveal wrapper ── */
const Reveal: React.FC<{
  children: React.ReactNode;
  className?: string;
  id?: string;
}> = ({ children, className, id }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      id={id}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

/* ── Icon map ── */
const ICON_MAP: Record<string, React.ElementType> = {
  FaBriefcase,
  FaGavel,
  FaHome,
  FaBuilding,
  FaUsers,
  FaGlobe,
  FaHeartbeat,
  FaTree,
  FaLightbulb,
  FaCalculator,
  FaUniversity,
  FaHardHat,
  FaShoppingCart,
  FaLandmark,
  FaBalanceScale,
  FaAnchor,
  FaNewspaper,
  FaMapMarkedAlt,
  FaScroll,
};

const PracticeAreas: React.FC = () => {
  const [activeArea, setActiveArea] = useState(PRACTICE_AREAS.areas[0].id);
  const contentRef = useRef<HTMLDivElement>(null);

  const area = PRACTICE_AREAS.areas.find((a) => a.id === activeArea)!;

  const handleSelect = (id: string) => {
    setActiveArea(id);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  };

  return (
    <main className={styles.page}>
      <SEO path="/practice-areas" />

      {/* Ambient glows */}
      <div className={`${styles.ambient} ${styles.ambient1}`} />
      <div className={`${styles.ambient} ${styles.ambient2}`} />

      {/* ── Hero ── */}
      <div className={styles.heroWrapper}>
        <motion.div
          className={styles.heroInner}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <div className={styles.heroContent}>
            <div className={styles.heroText}>
              <div className={styles.heroBadge}>
                <div className={styles.badgeDot} />
                <span>{PRACTICE_AREAS.hero.label}</span>
              </div>
              <h1 className={styles.sectionTitle}>
                {PRACTICE_AREAS.hero.title}
              </h1>
              <p className={styles.sectionSub}>{PRACTICE_AREAS.hero.subtitle}</p>
            </div>
          </div>
        </motion.div>
      </div>

      <div className={styles.sectionDivider} />

      {/* ── Explorer: Left TOC + Right Content ── */}
      <section className={styles.section}>
        <Reveal className={styles.explorerLayout}>
          {/* Left Panel — Practice Area List */}
          <div className={styles.areaList}>
            {PRACTICE_AREAS.areas.map((a) => {
              const IconComponent = ICON_MAP[a.icon] || FaBriefcase;
              const isActive = activeArea === a.id;
              return (
                <button
                  key={a.id}
                  className={`${styles.areaListItem} ${isActive ? styles.areaListItemActive : ''}`}
                  onClick={() => handleSelect(a.id)}
                >
                  <div className={`${styles.areaListIcon} ${isActive ? styles.areaListIconActive : ''}`}
                    style={isActive ? { background: `${a.colour}18`, borderColor: `${a.colour}40`, color: a.colour } : {}}
                  >
                    <IconComponent />
                  </div>
                  <div className={styles.areaListText}>
                    <span className={styles.areaListTitle}>{a.title}</span>
                    <span className={styles.areaListDesc}>{a.shortDesc}</span>
                  </div>
                  {isActive && <div className={styles.activeIndicator} />}
                </button>
              );
            })}
          </div>

          {/* Right Panel — Practice Area Content */}
          <div className={styles.areaContent} ref={contentRef}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeArea}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Header */}
                <div className={styles.areaDetailHeader}>
                  <div
                    className={styles.areaDetailIcon}
                    style={{
                      background: `${area.colour}18`,
                      border: `1px solid ${area.colour}40`,
                      color: area.colour,
                    }}
                  >
                    {React.createElement(ICON_MAP[area.icon] || FaBriefcase)}
                  </div>
                  <h2 className={styles.areaDetailTitle}>{area.title}</h2>
                </div>

                {/* Overview */}
                <p className={styles.areaOverview}>{area.overview}</p>

                {/* Stats (a market may have none to show) */}
                {area.stats.length > 0 && (
                <div className={styles.statsRow}>
                  {area.stats.map((stat, i) => (
                    <div className={styles.statCard} key={i}>
                      <div className={styles.statValue}>{stat.value}</div>
                      <div className={styles.statLabel}>{stat.label}</div>
                    </div>
                  ))}
                </div>
                )}

                {/* Benefits */}
                <div className={styles.benefitsLabel}>How JurisTab Helps</div>
                <div className={styles.benefitsGrid}>
                  {area.benefits.map((benefit, i) => (
                    <div className={styles.benefitCard} key={i}>
                      <h4 className={styles.benefitTitle}>{benefit.title}</h4>
                      <p className={styles.benefitDesc}>{benefit.description}</p>
                    </div>
                  ))}
                </div>

                {/* Dedicated workflow suite (only the areas that have one) */}
                {area.suite && (
                  <div className={styles.suiteBlock}>
                    <div className={styles.suiteBlockHead}>
                      <div className={styles.benefitsLabel}>{PRACTICE_AREAS.suiteLabel}</div>
                      <span className={styles.suiteBlockCount}>{area.suite.count}</span>
                    </div>
                    <p className={styles.suiteBlockDesc}>{area.suite.desc}</p>
                    <ul className={styles.suiteBlockList}>
                      {area.suite.services.map((svc) => (
                        <li key={svc}>{svc}</li>
                      ))}
                    </ul>
                    <Link to={PRACTICE_AREAS.suiteLink} className={styles.suiteBlockLink}>
                      {PRACTICE_AREAS.suiteLinkText}
                    </Link>
                  </div>
                )}

                {/* Example Query (only where the market sells research) */}
                {area.exampleQuery && (
                <div className={styles.exampleQuery}>
                  <span className={styles.exampleQueryLabel}>
                    <FaSearch style={{ marginRight: 6 }} />
                    Try this search
                  </span>
                  <span className={styles.exampleQueryText}>{area.exampleQuery}</span>
                </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </Reveal>
      </section>

      <div className={styles.sectionDivider} />

      {/* ── CTA ── */}
      <div className={styles.ctaSection}>
        <Reveal className={styles.ctaBanner}>
          <div>
            <h2>{PRACTICE_AREAS.cta.title}</h2>
            <p>{PRACTICE_AREAS.cta.subtitle}</p>
          </div>
          <div className={styles.ctaActions}>
            <a
              href={PRACTICE_AREAS.cta.ctaPrimaryLink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.btnLarge}
            >
              {PRACTICE_AREAS.cta.ctaPrimary}
              <FaArrowRight className={styles.ctaButtonIcon} />
            </a>
            <Link to={PRACTICE_AREAS.cta.ctaSecondaryLink} className={styles.btnOutline}>
              {PRACTICE_AREAS.cta.ctaSecondary}
            </Link>
          </div>
        </Reveal>
      </div>
    </main>
  );
};

export default PracticeAreas;
